import { useState } from 'react';
import { UserModel, UserRoleEnum, UserSettingsModel, UserStatusEnum, TeacherOnboardingStatusEnum } from '../../../interfaces/user.interface';
import { updateUserProfileService } from './services/AdminForms.service';

interface UserDetailModalProps {
  user: UserModel;
  isUserDetailModalOpen: boolean;
  onClose: () => void;
  refresh?: () => void;
}

const UserDetailModal = ({ user, isUserDetailModalOpen, onClose, refresh }: UserDetailModalProps) => {
  const [updatedData, setUpdatedData] = useState<UserModel>({ ...user });
  const [loader, setLoader] = useState(false);

  const isTeacher = user.role === UserRoleEnum.teacher || !!user.settings?.USER_SETTING_TEACHER_OR_GROUP;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setUpdatedData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleChangeSettings = (e: React.ChangeEvent<HTMLInputElement>, key: string) => {
    const { checked } = e.target;
    setUpdatedData((prevData) => ({
      ...prevData,
      settings: {
        ...prevData.settings,
        [key]: checked,
      } as UserSettingsModel,
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const data = {
      email: updatedData.email!,
      firstName: updatedData.firstName!,
      lastName: updatedData.lastName!,
      status: updatedData.status || '',
      settings: updatedData.settings,
      ...(isTeacher ? { teacherOnboardingStatus: updatedData.teacherOnboardingStatus } : {}),
    };

    await updateUserProfileService(data, setLoader, onClose);
    refresh && refresh();
  };

  // Settings keys that are editable — filter teacher-only keys for parents
  const editableSettingKeys = Object.keys({
    USER_SETTING_MARKETING_EMAILS: false,
    USER_SETTING_PROMOTIONAL_EMAILS: false,
    ...(isTeacher ? { USER_SETTING_TEACHER_OR_GROUP: false } : {}),
  });

  return (
    <div className={`max-w-5xl mx-auto min-h-screen modal ${isUserDetailModalOpen ? 'block' : 'hidden'}`}>
      <h1 className="text-storyblue text-lg md:text-2xl font-bold text-start mb-4">Edit Customer</h1>
      <form onSubmit={handleSubmit}>
        <div className="w-full flex justify-between items-center">
          <label htmlFor="email">Email:</label>
          <input
            className="mt-4 p-2 w-[70%] bg-gray-100 rounded-md text-gray-500"
            type="email"
            disabled
            id="email"
            name="email"
            value={updatedData.email}
          />
        </div>

        <div className="w-full flex justify-between items-center">
          <label htmlFor="firstName">First Name:</label>
          <input
            className="mt-4 p-2 w-[70%] bg-white rounded-md"
            type="text"
            id="firstName"
            name="firstName"
            value={updatedData.firstName}
            onChange={handleChange}
          />
        </div>

        <div className="w-full flex justify-between items-center">
          <label htmlFor="lastName">Last Name:</label>
          <input
            className="mt-4 p-2 w-[70%] bg-white rounded-md"
            type="text"
            id="lastName"
            name="lastName"
            value={updatedData.lastName}
            onChange={handleChange}
          />
        </div>

        <div className="w-full flex justify-between items-center">
          <label htmlFor="status">Status:</label>
          <select
            id="status"
            name="status"
            value={updatedData.status}
            onChange={handleChange}
            className="mt-4 p-2 w-[70%] bg-white rounded-md"
          >
            <option value={UserStatusEnum.ACTIVE}>Active</option>
            <option value={UserStatusEnum.INACTIVE}>Inactive</option>
          </select>
        </div>

        {isTeacher && (
          <div className="w-full flex justify-between items-center">
            <label htmlFor="teacherOnboardingStatus">Onboarding Status:</label>
            <select
              id="teacherOnboardingStatus"
              name="teacherOnboardingStatus"
              value={updatedData.teacherOnboardingStatus || ''}
              onChange={handleChange}
              className="mt-4 p-2 w-[70%] bg-white rounded-md"
            >
              <option value="">—</option>
              <option value={TeacherOnboardingStatusEnum.BookOnboarding}>Book Onboarding</option>
              <option value={TeacherOnboardingStatusEnum.TeacherInfo}>Teacher Info</option>
              <option value={TeacherOnboardingStatusEnum.Complete}>Complete</option>
            </select>
          </div>
        )}

        <div className="md:ml-[15rem]">
          {editableSettingKeys.map((key) => (
            <div key={key} className="w-full flex items-center">
              <input
                className="mt-4 p-2 w-[15%] cursor-pointer bg-white rounded-md"
                type="checkbox"
                id={key}
                name={key}
                checked={!!updatedData?.settings?.[key]}
                onChange={(e) => handleChangeSettings(e, key)}
              />
              <label className="capitalize mt-2 pt-2 cursor-pointer" htmlFor={key}>
                {key.replace(/_/g, ' ').replace('USER', '').toLowerCase()}
              </label>
            </div>
          ))}
        </div>

        <div className="flex gap-3">
          <button
            className="w-full mt-10 flex justify-center border-storyblueSecondLightText border text-storyblueSecondLightText hover:text-storyblueSecondLightText font-bold rounded-md text-base p-2"
            onClick={onClose}
            type="button"
          >
            Close
          </button>
          <button
            className="w-full mt-10 flex justify-center bg-storyblueSecondLightText text-white hover:text-white font-bold rounded-md text-base p-2"
            type="submit"
          >
            Save
          </button>
        </div>
      </form>
    </div>
  );
};

export default UserDetailModal;
